import type {
  AdEngBiddersPayload,
  AdEngBiddersTrackData,
  BidStatus,
} from "common-types";
import { send } from "./sender";
import type { SlotUid } from "../utils/slotUid";
import type { BiddersSenderBody } from "../types";
import {
  getArticleId,
  getBeaconId,
  getPageLang,
  getSessionId,
  getTrackingPv,
  getTrackingPvGlobal,
  getTrackingPvUid,
  getUserId,
  getWikiId,
  getWikiName,
  getWikiSkin,
} from "../utils/beaconParams";
import { getExpBucket } from "utils";

// Extend the Window interface to include fandomAds
// TODO: should consolidate this definition with the one in ad-engine/src/core/FandomAds and move to common-types
declare global {
  interface Window {
    fandomAds?: {
      version?: string;
    };
  }
}

export function sendBidders(
  { payload }: AdEngBiddersTrackData,
  slotUid: SlotUid,
): void {
  /**
   * ETL function that documents what fields are expected in the bid data:
   * https://github.com/Wikia/crossbow/blob/master/mwaa/dags/clickstream_adeng_events/sql/hive/recreate_insert/01_fact_adengbidders_events_recreate_insert.sql#L3-L20
   *
   * TODO: At some point we can link instead to documentation of the schema, but this does not exist yet.
   */
  const nowDate = new Date();

  const body: BiddersSenderBody = {
    status: getStatus(payload),
    name: getBidderName(payload),
    slot_name: payload.slotName,
    rv: getRv(payload),

    auction_id: payload.bidInfo.auctionId ?? "",
    rejection_reason: payload.bidInfo.rejectionReason ?? "",
    odyssey_version: window.fandomAds?.version ?? "unknown",

    response_time: getTimeToRespond(payload),

    size: getSize(payload),
    price: getPrice(payload),

    additional_flags: getFlags(payload),
    slot_id: slotUid.getUid(payload.slotName),
    pos: payload.baseSlotName,

    timestamp: nowDate.getTime(),

    session_id: getSessionId(),
    pv_number: getTrackingPv(),
    pv_number_global: getTrackingPvGlobal(),
    pv_unique_id: getTrackingPvUid(),
    beacon: getBeaconId(),
    c: getWikiId(),
    x: getWikiName(),
    lc: getPageLang(),
    s: getWikiSkin(),
    ua: window.navigator.userAgent,
    u: getUserId(),
    a: getArticleId(),
    n: -1,
    exp_bucket: getExpBucket(),
    full_auction: getFullAuction(payload),
  };

  send("adengbidders", body);
}

function getStatus(payload: AdEngBiddersPayload): BidStatus {
  /**
   * status field only tracks if the bid was received on time or not
   */
  if (payload.bidInfo?.status) {
    return payload.bidInfo.status;
  } else if (payload.bidInfo?.timedOut === true) {
    return "timedout";
  } else if (payload.bidInfo?.timedOut === false) {
    return "on_time";
  }
  return "unknown";
}

function getBidderName(payload: AdEngBiddersPayload): string {
  return payload.bidInfo?.bidderName ?? "unknown";
}

function getSize(payload: AdEngBiddersPayload): string {
  return payload.bidInfo?.size ?? "unknown";
}

function getPrice(payload: AdEngBiddersPayload): string {
  return payload.bidInfo?.price ?? "unknown";
}

function getTimeToRespond(payload: AdEngBiddersPayload): number {
  return payload.bidInfo?.timeToRespond ?? 0;
}

function populateFlagsWithBuyerId(payload: AdEngBiddersPayload): void {
  if (payload?.bidInfo?.buyerId) {
    payload.flags?.push(`buyer_id=${payload.bidInfo?.buyerId}`);
  }
}

function getFlags(payload: AdEngBiddersPayload): string {
  payload.flags = [];

  populateFlagsWithBuyerId(payload);

  return (payload.flags ?? []).join(";");
}

function getRv(payload: AdEngBiddersPayload): number {
  return payload.rv ?? -1;
}

function getFullAuction(payload: AdEngBiddersPayload): "yes" | "no" {
  if (payload.bidInfo?.fullAuction === true) {
    return "yes";
  }
  // If not definitively recorded as tracking a full auction, we assume it is not
  return "no";
}
